import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { trackPixelEvent } from "../utils/metaPixel";
import "./BookingForm.css";

const BookingForm = () => {
  const navigate = useNavigate();

  const [bookingData, setBookingData] = useState(() => {
    // Try to restore from location state or localStorage
    const saved = localStorage.getItem("lastBookingData");

    // Default to Now + 6 Hours
    const nowPlus8 = new Date(new Date().getTime() + 8 * 60 * 60 * 1000);
    // toLocaleDateString('en-CA') gives YYYY-MM-DD in local time
    const defaultDate = nowPlus8.toLocaleDateString("en-CA");
    // toLocaleTimeString('en-GB', { ... }) gives HH:MM in 24h format
    const defaultTime = nowPlus8.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    let initial = null;
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.pickupDate && parsed.pickupTime) {
          const savedDT = new Date(`${parsed.pickupDate}T${parsed.pickupTime}`);
          if (savedDT >= nowPlus8) {
            initial = parsed;
          }
        }
      } catch (e) {}
    }

    if (!initial) {
      initial = {
        pickupDate: defaultDate,
        pickupTime: defaultTime,
        returnDate: "",
        returnTime: "",
        pickupLocation: "",
        dropoffLocation: "",
        transferType: "one-way",
        waitingHours: "no-waiting",
        passengers: 1,
        luggage: 0,
      };
    }
    return initial;
  });
  const [distanceInfo, setDistanceInfo] = useState(() => {
    const saved = localStorage.getItem("lastDistanceInfo");
    return saved ? JSON.parse(saved) : { distance: "0 KM", duration: "0 Hour" };
  });
  const [loading, setLoading] = useState(false);

  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const watchId = useRef(null);
  const userMarker = useRef(null);
  const accuracyCircle = useRef(null);
  const lastGeocodedPos = useRef(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isPickupFocused, setIsPickupFocused] = useState(false);
  const [isDropoffFocused, setIsDropoffFocused] = useState(false);
  const bookingDataRef = useRef(bookingData);
  const hasAnnounced200m = useRef(false);
  const hasAnnouncedArrival = useRef(false);

  // Dynamic Intermediate Stops
  const [stops, setStops] = useState(() => {
    try {
      const saved = localStorage.getItem("lastBookingStops");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const stopMarkersRef = useRef([]);

  useEffect(() => {
    localStorage.setItem("lastBookingStops", JSON.stringify(stops));
  }, [stops]);

  const isNightPickupTime = (timeStr) => {
    if (!timeStr) return false;
    let hours = 0;
    const trimmed = String(timeStr).trim();
    const match12 = trimmed.match(/(\d{1,2})[:\.](\d{2})\s*(am|pm)/i);
    if (match12) {
      hours = parseInt(match12[1], 10);
      const meridian = match12[3].toLowerCase();
      if (meridian === "pm" && hours < 12) hours += 12;
      if (meridian === "am" && hours === 12) hours = 0;
    } else {
      const match24 = trimmed.match(/^(\d{1,2})[:\.](\d{2})/);
      if (match24) hours = parseInt(match24[1], 10);
    }
    return hours >= 21 || hours < 5;
  };

  const handleAddStop = () => {
    setStops((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), address: "", lat: null, lng: null },
    ]);
  };

  const handleRemoveStop = (index) => {
    setStops((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStopChange = (index, value) => {
    setStops((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], address: value };
      }
      return next;
    });
  };

  const handleClearStop = (index) => {
    setStops((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], address: "", lat: null, lng: null };
      }
      return next;
    });
  };

  const attachStopAutocomplete = (index) => (el) => {
    if (!el || !window.google || !window.google.maps || !window.google.maps.places) return;
    if (el._hasAutocomplete) return;
    el._hasAutocomplete = true;

    const options = {
      componentRestrictions: { country: "in" },
      fields: ["address_components", "geometry", "name", "formatted_address"],
      types: ["establishment", "geocode"],
    };

    const autocomplete = new window.google.maps.places.Autocomplete(el, options);
    if (mapInstance.current) {
      autocomplete.bindTo("bounds", mapInstance.current);
    }

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        let address = place.formatted_address || place.name;
        if (
          place.name &&
          place.formatted_address &&
          !place.formatted_address.includes(place.name)
        ) {
          address = `${place.name}, ${place.formatted_address}`;
        }
        setStops((prev) => {
          const next = [...prev];
          if (next[index]) {
            next[index] = {
              ...next[index],
              address: address,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
          }
          return next;
        });
      }
    });
  };

  useEffect(() => {
    bookingDataRef.current = bookingData;
  }, [bookingData]);

  // Cleanup tracking on unmount
  useEffect(() => {
    return () => {
      if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
    };
  }, []);

  const stopTracking = () => {
    if (watchId.current) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setIsTracking(false);
    setLoading(false);
    if (accuracyCircle.current) accuracyCircle.current.setMap(null);
    // We keep the marker to show last known location
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    setIsTracking(true);
    setLoading(true); // Initial load

    const options = {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    };

    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        const {
          latitude: lat,
          longitude: lng,
          accuracy,
          heading,
          speed,
        } = position.coords;

        // 2. Accuracy Checks
        if (accuracy > 2000) {
          toast.warning(
            `Low GPS accuracy (${Math.round(accuracy)}m). Location may be approximate.`,
          );
        }

        // 3. Update Map Visuals (Marker & Circle)
        if (mapInstance.current) {
          const latLng = new window.google.maps.LatLng(lat, lng);

          // Determine Icon (Arrow if moving/heading available, else Dot)
          const icon =
            heading !== null &&
            heading !== undefined &&
            !isNaN(heading) &&
            speed > 1
              ? {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 6,
                  fillColor: "#4285F4",
                  fillOpacity: 1,
                  strokeColor: "white",
                  strokeWeight: 2,
                  rotation: heading,
                }
              : {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#4285F4",
                  fillOpacity: 1,
                  strokeColor: "white",
                  strokeWeight: 2,
                };

          // User Marker
          if (!userMarker.current) {
            userMarker.current = new window.google.maps.Marker({
              position: latLng,
              map: mapInstance.current,
              icon: icon,
              title: "You are here",
              zIndex: 999,
            });
          } else {
            userMarker.current.setPosition(latLng);
            userMarker.current.setIcon(icon);
          }

          // Accuracy Circle
          if (!accuracyCircle.current) {
            accuracyCircle.current = new window.google.maps.Circle({
              strokeColor: "#4285F4",
              strokeOpacity: 0.3,
              strokeWeight: 1,
              fillColor: "#4285F4",
              fillOpacity: 0.15,
              map: mapInstance.current,
              center: latLng,
              radius: accuracy,
            });
          } else {
            accuracyCircle.current.setCenter(latLng);
            accuracyCircle.current.setRadius(accuracy);
          }

          // ✅ No voice announcements on website
          // Arrival info shown via toast only
          const bData = bookingDataRef.current;
          if (bData.dropoffLat && bData.dropoffLng) {
            const distToDropoff =
              window.google.maps.geometry.spherical.computeDistanceBetween(
                new window.google.maps.LatLng(lat, lng),
                new window.google.maps.LatLng(
                  bData.dropoffLat,
                  bData.dropoffLng,
                ),
              );

            if (
              distToDropoff <= 200 &&
              distToDropoff > 50 &&
              !hasAnnounced200m.current
            ) {
              toast.info("Approaching destination (200m)");
              hasAnnounced200m.current = true;
            } else if (distToDropoff <= 50 && !hasAnnouncedArrival.current) {
              toast.success("You have arrived!");
              hasAnnouncedArrival.current = true;
            } else if (distToDropoff > 200) {
              hasAnnounced200m.current = false;
              hasAnnouncedArrival.current = false;
            }
          }

          // Auto-center (Snap to User) if tracking and moved significantly OR first fix
          if (!lastGeocodedPos.current) {
            mapInstance.current.setCenter(latLng);
            mapInstance.current.setZoom(17);
          }
        }

        // 4. Distanced-based Actions (Geocoding & ETA)
        const dist = lastGeocodedPos.current
          ? window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(
                lastGeocodedPos.current.lat,
                lastGeocodedPos.current.lng,
              ),
              new window.google.maps.LatLng(lat, lng),
            )
          : 1000;

        // Threshold: 20 meters
        if (dist > 20) {
          performReverseGeocode(lat, lng, "pickup"); // Explicitly update pickup
          lastGeocodedPos.current = { lat, lng };

          // 5. Live ETA (Distance Matrix)
          if (bookingData.dropoffLocation) {
            const distService = new window.google.maps.DistanceMatrixService();
            distService.getDistanceMatrix(
              {
                origins: [{ lat, lng }],
                destinations: [bookingData.dropoffLocation],
                travelMode: "DRIVING",
              },
              (response, status) => {
                if (
                  status === "OK" &&
                  response.rows[0].elements[0].status === "OK"
                ) {
                  const element = response.rows[0].elements[0];
                  setDistanceInfo({
                    distance: element.distance.text,
                    duration: element.duration.text,
                  });
                  // Optional: Update localStorage if needed
                  // localStorage.setItem('lastDistanceInfo', ...);
                }
              },
            );
          }
        }

        setLoading(false); // First fix done
      },
      (error) => {
        console.error("WatchPosition Error:", error);
        if (error.code === 1) toast.error("Location permission denied.");
        else if (error.code === 2)
          toast.warn("Position unavailable. Retrying...");
        else if (error.code === 3) toast.warn("GPS timeout. Retrying...");
        setLoading(false);
      },
      options,
    );
  };

  // Extracts District & Taluka from Google address_components and appends to address
  const parseAddressWithDetails = (result) => {
    const components = result.address_components || [];
    let district = "";
    let taluka = "";
    components.forEach((c) => {
      if (c.types.includes("administrative_area_level_2"))
        district = c.long_name;
      if (c.types.includes("administrative_area_level_3")) taluka = c.long_name;
    });
    let base = result.formatted_address || "";
    const suffix = [taluka && `Ta: ${taluka}`, district && `Dist: ${district}`]
      .filter(Boolean)
      .join(", ");
    return suffix ? `${base} (${suffix})` : base;
  };

  // ✅ Uber-style: Use Google Geocoder formatted_address — NO Plus Codes, NO landmarks
  // Format: "Vrindavan, Godrej Garden City, Ahmedabad, Gujarat 382470, India"
  const performReverseGeocode = (lat, lng, type = "pickup") => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        // Plus Code pattern: e.g. "4H95+MFW" or "GCJ2+MF"
        const plusCodeRegex = /^[0-9A-Z]{4}\+[0-9A-Z]{2,}/;

        // 1. Try to find a result that does NOT start with a Plus Code
        let bestResult = results.find(
          (r) => !plusCodeRegex.test(r.formatted_address),
        );

        // 2. Fallback: use first result but strip the Plus Code prefix
        let finalAddress;
        if (bestResult) {
          finalAddress = parseAddressWithDetails(bestResult);
        } else {
          // Strip "XXXX+XX, " from beginning of address
          finalAddress = parseAddressWithDetails(results[0]).replace(
            /^[0-9A-Z]{4}\+[0-9A-Z]{2,},?\s*/,
            "",
          );
        }

        setBookingData((prev) => {
          const newData = { ...prev };
          if (type === "pickup") {
            newData.pickupLocation = finalAddress;
            newData.pickupLat = lat;
            newData.pickupLng = lng;
          } else if (type === "dropoff") {
            newData.dropoffLocation = finalAddress;
            newData.dropoffLat = lat;
            newData.dropoffLng = lng;
          }
          return newData;
        });

        // Update input field visually
        if (type === "pickup" && pickupRef.current) {
          pickupRef.current.value = finalAddress;
        } else if (type === "dropoff" && dropoffRef.current) {
          dropoffRef.current.value = finalAddress;
        }
      }
    });
  };
  // Trigger route calculation if data is restored
  useEffect(() => {
    if (bookingData.pickupLocation && bookingData.dropoffLocation) {
      const timer = setTimeout(() => {
        calculateRoute();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [bookingData.pickupLocation, bookingData.dropoffLocation]);

  useEffect(() => {
    // Initialize Map
    if (mapRef.current && !mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 23.0225, lng: 72.5714 }, // Ahmedabad Default
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
      });

      // Initialize DirectionsRenderer
      const renderer = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true, // We will use our own custom draggable markers
      });
      renderer.setMap(mapInstance.current);
      mapInstance.current.directionsRenderer = renderer;
    }

    // Initialize Markers if not exists
    if (mapInstance.current && !pickupRef.current.marker) {
      const marker = new window.google.maps.Marker({
        map: mapInstance.current,
        draggable: true,
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
          scaledSize: new window.google.maps.Size(40, 40),
        },
        title: "Pickup Location",
      });
      marker.addListener("dragend", () => {
        const pos = marker.getPosition();
        performReverseGeocode(pos.lat(), pos.lng(), "pickup");
      });
      pickupRef.current.marker = marker;
    }

    if (mapInstance.current && !dropoffRef.current.marker) {
      const marker = new window.google.maps.Marker({
        map: mapInstance.current,
        draggable: true,
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
          scaledSize: new window.google.maps.Size(40, 40),
        },
        title: "Dropoff Location",
      });
      marker.addListener("dragend", () => {
        const pos = marker.getPosition();
        performReverseGeocode(pos.lat(), pos.lng(), "dropoff");
      });
      dropoffRef.current.marker = marker;
    }

    // Initialize Autocomplete
    const options = {
      componentRestrictions: { country: "in" },
      fields: [
        "address_components",
        "geometry",
        "icon",
        "name",
        "formatted_address",
        "types",
      ],
      types: ["establishment", "geocode"],
    };

    const pickupAutocomplete = new window.google.maps.places.Autocomplete(
      pickupRef.current,
      options,
    );
    const dropoffAutocomplete = new window.google.maps.places.Autocomplete(
      dropoffRef.current,
      options,
    );

    // Bind Autocomplete to Map Bounds (Prioritize local results)
    if (mapInstance.current) {
      pickupAutocomplete.bindTo("bounds", mapInstance.current);
      dropoffAutocomplete.bindTo("bounds", mapInstance.current);
    }

    const AIRPORT_KW = [
      "airport",
      "airoport",
      "airpot",
      "air port",
      "sardar vallabhbhai",
      "svp airport",
    ];
    const checkAirport = (name) =>
      name && AIRPORT_KW.some((kw) => name.toLowerCase().includes(kw));

    pickupAutocomplete.addListener("place_changed", () => {
      const place = pickupAutocomplete.getPlace();
      if (place.geometry) {
        let address = place.formatted_address || place.name;
        if (
          place.name &&
          place.formatted_address &&
          !place.formatted_address.includes(place.name)
        ) {
          address = `${place.name}, ${place.formatted_address}`;
        }
        const isAirport =
          checkAirport(address) ||
          (place.types && place.types.includes("airport"));
        setBookingData((prev) => ({
          ...prev,
          pickupLocation: address,
          pickupLat: place.geometry.location.lat(),
          pickupLng: place.geometry.location.lng(),
          ...(isAirport ? { transferType: "airport" } : {}),
        }));
        if (mapInstance.current) {
          mapInstance.current.setCenter(place.geometry.location);
          mapInstance.current.setZoom(14);
        }
      }
    });

    dropoffAutocomplete.addListener("place_changed", () => {
      const place = dropoffAutocomplete.getPlace();
      if (place.geometry) {
        let address = place.formatted_address || place.name;
        if (
          place.name &&
          place.formatted_address &&
          !place.formatted_address.includes(place.name)
        ) {
          address = `${place.name}, ${place.formatted_address}`;
        }
        const isAirport =
          checkAirport(address) ||
          (place.types && place.types.includes("airport"));
        setBookingData((prev) => ({
          ...prev,
          dropoffLocation: address,
          dropoffLat: place.geometry.location.lat(),
          dropoffLng: place.geometry.location.lng(),
          ...(isAirport ? { transferType: "airport" } : {}),
        }));
      }
    });
  }, []);

  // Sync Markers with State
  useEffect(() => {
    if (!mapInstance.current) return;

    if (
      bookingData.pickupLat &&
      bookingData.pickupLng &&
      pickupRef.current.marker
    ) {
      const pos = new window.google.maps.LatLng(
        bookingData.pickupLat,
        bookingData.pickupLng,
      );
      pickupRef.current.marker.setPosition(pos);
      pickupRef.current.marker.setVisible(true);
    }

    if (
      bookingData.dropoffLat &&
      bookingData.dropoffLng &&
      dropoffRef.current.marker
    ) {
      const pos = new window.google.maps.LatLng(
        bookingData.dropoffLat,
        bookingData.dropoffLng,
      );
      dropoffRef.current.marker.setPosition(pos);
      dropoffRef.current.marker.setVisible(true);
    }
  }, [
    bookingData.pickupLat,
    bookingData.pickupLng,
    bookingData.dropoffLat,
    bookingData.dropoffLng,
  ]);

  // Sync Stop Markers with Map
  useEffect(() => {
    if (!mapInstance.current || !window.google) return;

    // Clear old stop markers
    stopMarkersRef.current.forEach((m) => m && m.setMap(null));
    stopMarkersRef.current = [];

    stops.forEach((stop, idx) => {
      if (stop.lat && stop.lng) {
        const marker = new window.google.maps.Marker({
          position: new window.google.maps.LatLng(stop.lat, stop.lng),
          map: mapInstance.current,
          title: `Stop ${idx + 1}: ${stop.address || ""}`,
          label: {
            text: String(idx + 1),
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "11px",
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#f59e0b",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });
        stopMarkersRef.current.push(marker);
      }
    });

    if (bookingData.pickupLocation && bookingData.dropoffLocation) {
      calculateRoute();
    }
  }, [stops]);

  const calculateRoute = () => {
    const isRoundTrip = bookingData.transferType === "round-trip";

    // ✅ Use exact lat/lng when available (from autocomplete/geocode)
    // so the route line connects EXACTLY to the marker pins — no visual gap
    const origin =
      bookingData.pickupLat && bookingData.pickupLng
        ? { lat: bookingData.pickupLat, lng: bookingData.pickupLng }
        : bookingData.pickupLocation;

    const destLatLng =
      bookingData.dropoffLat && bookingData.dropoffLng
        ? { lat: bookingData.dropoffLat, lng: bookingData.dropoffLng }
        : bookingData.dropoffLocation;

    const destination = isRoundTrip ? origin : destLatLng;

    if (!origin || !destLatLng || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();

    const waypoints = [];
    stops.forEach((s) => {
      if (s.lat && s.lng) {
        waypoints.push({
          location: new window.google.maps.LatLng(s.lat, s.lng),
          stopover: true,
        });
      } else if (s.address && s.address.trim()) {
        waypoints.push({ location: s.address.trim(), stopover: true });
      }
    });

    if (isRoundTrip) {
      waypoints.push({ location: destLatLng, stopover: true });
    }

    const reqData = {
      origin: origin,
      destination: destination,
      travelMode: window.google.maps.TravelMode.DRIVING,
    };

    if (waypoints.length > 0) {
      reqData.waypoints = waypoints;
    }

    // 1. Get Route for Map
    directionsService.route(reqData, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        // Update Map with path and fit bounds to show full route
        if (mapInstance.current && mapInstance.current.directionsRenderer) {
          mapInstance.current.directionsRenderer.setDirections(result);
          // Fit map to show entire route (pickup → dropoff)
          if (result.routes[0] && result.routes[0].bounds) {
            mapInstance.current.fitBounds(result.routes[0].bounds, 60); // 60px padding
          }
        }

        // 2. Calculate Distance/Duration
        let totalDistanceVal = 0;
        let totalDurationVal = 0;

        const routes = result.routes[0];
        routes.legs.forEach((leg) => {
          totalDistanceVal += leg.distance.value;
          totalDurationVal += leg.duration.value;
        });

        // Format nicely (convert meters to km, seconds to string)
        const totalDistKm = (totalDistanceVal / 1000).toFixed(1) + " km";

        // Convert seconds to hrs and mins
        const hrs = Math.floor(totalDurationVal / 3600);
        const mins = Math.round((totalDurationVal % 3600) / 60);
        const totalDurText =
          hrs > 0 ? `${hrs} hours ${mins} mins` : `${mins} mins`;

        const info = {
          distance: totalDistKm,
          duration: totalDurText,
        };

        setDistanceInfo(info);
        localStorage.setItem("lastDistanceInfo", JSON.stringify(info));
      } else {
        console.error("Directions request failed due to " + status);
      }
    });
  };

  // Re-calculate route when transferType changes
  useEffect(() => {
    if (bookingData.pickupLocation && bookingData.dropoffLocation) {
      calculateRoute();
    }
  }, [bookingData.transferType]);

  // Airport keywords to detect
  const AIRPORT_KEYWORDS = [
    "airport",
    "aéroport",
    "airoport",
    "airpot",
    "air port",
    "sardar vallabhbhai",
    "svp airport",
    "ahmedabad airport",
    "amd airport",
  ];

  const isAirportLocation = (loc) => {
    if (!loc) return false;
    const lower = loc.toLowerCase();
    return AIRPORT_KEYWORDS.some((kw) => lower.includes(kw));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newData = {
      ...bookingData,
      [name]: value,
    };

    // Auto-default return date & time when choosing round-trip
    if (name === "transferType" && value === "round-trip") {
      if (!newData.returnDate) {
        newData.returnDate = newData.pickupDate || new Date().toLocaleDateString("en-CA");
      }
      if (!newData.returnTime && newData.pickupTime) {
        const [ph, pm] = newData.pickupTime.split(":").map(Number);
        const rh = (ph + 4) % 24;
        newData.returnTime = `${String(rh).padStart(2, "0")}:${String(pm || 0).padStart(2, "0")}`;
      }
    }

    // Auto-detect Airport in pickup or dropoff location
    if (name === "pickupLocation" || name === "dropoffLocation") {
      const pickupVal =
        name === "pickupLocation" ? value : newData.pickupLocation;
      const dropoffVal =
        name === "dropoffLocation" ? value : newData.dropoffLocation;
      if (isAirportLocation(pickupVal) || isAirportLocation(dropoffVal)) {
        newData.transferType = "airport";
      }
    }

    setBookingData(newData);
    localStorage.setItem("lastBookingData", JSON.stringify(newData));

    // If location is cleared, reset distance/time
    if (
      (name === "pickupLocation" || name === "dropoffLocation") &&
      value === ""
    ) {
      const resetInfo = { distance: "0 KM", duration: "0 Hour" };
      setDistanceInfo(resetInfo);
      localStorage.setItem("lastDistanceInfo", JSON.stringify(resetInfo));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: 6 Hours gap check
    const now = new Date();
    const selectedDateTime = new Date(
      `${bookingData.pickupDate}T${bookingData.pickupTime}`,
    );
    const minBookingTime = new Date(now.getTime() + 8 * 60 * 60 * 1000); // Current time + 6 hours

    if (selectedDateTime < minBookingTime) {
      toast.error(
        "Bookings must be made at least 8 hours in advance. Please select a later time.",
      );
      return;
    }

    // TESTING: 30 KM minimum distance check commented out
    // if (distanceInfo && distanceInfo.distance) {
    //     let distStr = String(distanceInfo.distance).replace(/,/g, '').toLowerCase().trim();
    //     let distVal = 0;
    //
    //     if (distStr.includes('km')) {
    //         distVal = parseFloat(distStr.replace('km', '').trim());
    //     } else if (distStr.includes(' m')) { // meters, e.g. "500 m"
    //         distVal = parseFloat(distStr.replace('m', '').trim()) / 1000;
    //     } else {
    //         // Try parsing directly if just a number
    //         distVal = parseFloat(distStr);
    //     }
    //
    //     if (!isNaN(distVal) && distVal > 0) {
    //         if (distVal < 30) {
    //             toast.error(`Distance is ${distVal.toFixed(1)} KM. We only provide Outstation rides (Min 30 KM).`);
    //             return;
    //         }
    //     }
    // }

    // Round trip validation: returnDate & returnTime
    if (bookingData.transferType === "round-trip") {
      if (!bookingData.returnDate) {
        toast.error("Please select a Return Date for Round Trip.");
        return;
      }
      if (!bookingData.returnTime) {
        toast.error("Please select a Return Time for Round Trip.");
        return;
      }
      const pickupDT = new Date(
        `${bookingData.pickupDate}T${bookingData.pickupTime}`,
      );
      const returnDT = new Date(
        `${bookingData.returnDate}T${bookingData.returnTime}`,
      );
      if (returnDT <= pickupDT) {
        if (bookingData.returnDate === bookingData.pickupDate) {
          toast.error("For same-day Round Trip, Return Time must be later than Pickup Time.");
        } else {
          toast.error("Return Date & Time must be after the Pickup Date & Time.");
        }
        return;
      }
    }

    // Navigate to the Taxi listing page with the form data and calculated distance
    setLoading(true);
    trackPixelEvent("Search", {
      content_category: bookingData.transferType || "one-way",
      content_name: `${bookingData.pickupLocation || ""} to ${bookingData.dropoffLocation || ""}`,
      search_string: `${bookingData.pickupLocation || ""} - ${bookingData.dropoffLocation || ""}`,
    });
    setTimeout(() => {
      navigate("/taxi", {
        state: {
          ...bookingData,
          stops: stops.filter((s) => s.address && s.address.trim()),
          intermediate_stops: stops.filter((s) => s.address && s.address.trim()),
          tripType: bookingData.transferType,
          returnDate:
            bookingData.transferType === "round-trip"
              ? bookingData.returnDate
              : null,
          returnTime:
            bookingData.transferType === "round-trip"
              ? bookingData.returnTime
              : null,
          distanceInfo,
        },
      });
      setLoading(false);
    }, 500); // Artificial delay to show loader
  };

  return (
    <div className="booking-widget-main">
      <div
        className="container-fluid"
        style={{ maxWidth: "1400px", padding: "0 20px" }}
      >
        <div className="booking-widget-container">
          <div className="booking-row">
            {/* Left Side: Form */}
            <div className="booking-left">
              <form onSubmit={handleSubmit}>
                <h4
                  className="booking-title"
                  style={{
                    textAlign: "center",
                    marginBottom: "13px",
                    fontSize: "1.2rem",
                    color: "#49ba09",
                    fontWeight: "bold",
                  }}
                >
                  Book your Car in MilesGoRide
                </h4>

                {/* Transfer Type - Moved to Top */}
                <div className="form-group-custom">
                  <label>Transfer Type</label>
                  <div className="input-with-icon select-wrapper">
                    <i className="fas fa-exchange-alt"></i>
                    <select
                      name="transferType"
                      className="form-control-custom"
                      value={bookingData.transferType}
                      onChange={handleChange}
                    >
                      <option value="one-way">One Way</option>
                      <option value="round-trip">Round Trip</option>
                      <option value="airport">Airport</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  {/* Pickup Date */}
                  <div className="col-12 col-md-6">
                    <div className="form-group-custom">
                      <label>Pickup Date</label>
                      <div className="input-with-icon date-time-wrapper">
                        <i className="far fa-calendar-alt date-time-icon"></i>
                        <input
                          type="date"
                          name="pickupDate"
                          className="form-control-custom date-time-input"
                          value={bookingData.pickupDate}
                          onChange={handleChange}
                          required
                          min={new Date(new Date().getTime() + 8 * 60 * 60 * 1000).toLocaleDateString("en-CA")}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Pickup Time */}
                  <div className="col-12 col-md-6">
                    <div className="form-group-custom">
                      <label>Pickup Time</label>
                      <div className="input-with-icon date-time-wrapper">
                        <i className="far fa-clock date-time-icon"></i>
                        <input
                          type="time"
                          name="pickupTime"
                          className="form-control-custom date-time-input"
                          value={bookingData.pickupTime}
                          onChange={handleChange}
                          required
                          min={
                            bookingData.pickupDate ===
                            new Date(new Date().getTime() + 8 * 60 * 60 * 1000).toLocaleDateString("en-CA")
                              ? new Date(
                                  new Date().getTime() + 8 * 60 * 60 * 1000,
                                ).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : undefined
                          }
                        />
                      </div>
                      {isNightPickupTime(bookingData.pickupTime) && (
                        <div style={{
                          fontSize: "11px",
                          color: "#b45309",
                          backgroundColor: "#fef3c7",
                          border: "1px solid #fde68a",
                          borderRadius: "6px",
                          padding: "3px 8px",
                          marginTop: "5px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          fontWeight: "600"
                        }}>
                          <span>🌙 Night Charge (9 PM - 5 AM): Applicable</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Return Date & Time — show only for Round Trip */}
                {bookingData.transferType === "round-trip" && (
                  <div className="row round-trip-fields">
                    {/* Return Date */}
                    <div className="col-12 col-md-6">
                      <div className="form-group-custom">
                        <label>Return Date</label>
                        <div className="input-with-icon date-time-wrapper">
                          <i className="far fa-calendar-check date-time-icon"></i>
                          <input
                            type="date"
                            name="returnDate"
                            className="form-control-custom date-time-input return-field"
                            value={bookingData.returnDate}
                            onChange={handleChange}
                            required
                            min={
                              bookingData.pickupDate ||
                              new Date().toLocaleDateString("en-CA")
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {/* Return Time */}
                    <div className="col-12 col-md-6">
                      <div className="form-group-custom">
                        <label>Return Time</label>
                        <div className="input-with-icon date-time-wrapper">
                          <i className="far fa-clock date-time-icon"></i>
                          <input
                            type="time"
                            name="returnTime"
                            className="form-control-custom date-time-input return-field"
                            value={bookingData.returnTime}
                            onChange={handleChange}
                            required
                            min={
                              bookingData.returnDate === bookingData.pickupDate
                                ? bookingData.pickupTime
                                : undefined
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pickup Location */}
                <div className="form-group-custom">
                  <label>Pickup Location</label>
                  <div
                    className="input-with-icon"
                    style={{ position: "relative" }}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    <input
                      ref={pickupRef}
                      type="text"
                      name="pickupLocation"
                      className="form-control-custom"
                      placeholder="Enter Pick-Up Location"
                      value={bookingData.pickupLocation}
                      onChange={handleChange}
                      onFocus={() => setIsPickupFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setIsPickupFocused(false), 200)
                      }
                      style={{
                        paddingRight: "45px",
                        display:
                          !isPickupFocused && bookingData.pickupLocation
                            ? "none"
                            : "block",
                      }}
                      required
                    />
                    {!isPickupFocused && bookingData.pickupLocation && (
                      <div
                        className="form-control-custom"
                        style={{
                          height: "auto",
                          minHeight: "34px",
                          paddingRight: "45px",
                          paddingLeft: "35px",
                          paddingTop: "2px",
                          paddingBottom: "2px",
                          cursor: "text",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          backgroundColor: "var(--bg-offset)",
                          fontSize: "11px",
                          lineHeight: "1.2",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          setIsPickupFocused(true);
                          setTimeout(() => {
                            if (pickupRef.current) pickupRef.current.focus();
                          }, 10);
                        }}
                      >
                        {bookingData.pickupLocation}
                      </div>
                    )}
                    {bookingData.pickupLocation && (
                      <i
                        className="fas fa-times"
                        style={{
                          position: "absolute",
                          right: "15px",
                          left: "auto",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#999",
                          padding: "10px",
                          pointerEvents: "auto",
                          zIndex: 10,
                        }}
                        onClick={() => {
                          setBookingData((prev) => ({
                            ...prev,
                            pickupLocation: "",
                            pickupLat: null,
                            pickupLng: null,
                          }));
                          if (pickupRef.current) pickupRef.current.value = "";
                        }}
                      ></i>
                    )}
                  </div>
                  <button
                    type="button"
                    style={{
                      marginTop: "5px",
                      fontSize: "0.8rem",
                      background: "none",
                      border: "none",
                      color: "#49ba09",
                      cursor: "pointer",
                      padding: 0,
                    }}
                    onClick={() => {
                      if (isTracking) {
                        stopTracking();
                      } else {
                        startLocationTracking();
                      }
                    }}
                  >
                    {/* <i className={`fas fa-map-marker-alt ${loading ? 'fa-spin' : ''}`} style={{ marginRight: '5px' }}></i> */}
                    {isTracking ? "Stop Tracking" : "Use Current Location"}
                  </button>
                </div>

                {/* Intermediate Stops (Multiple Pickup / Via Stations) */}
                {stops.map((stop, index) => (
                  <div key={stop.id || index} className="form-group-custom stop-item-container">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <label style={{ margin: 0, fontSize: "12px", color: "#d97706", fontWeight: "700" }}>
                        📍 Stop {index + 1} (Via Station)
                      </label>
                      <button
                        type="button"
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          fontSize: "11px",
                          fontWeight: "700",
                          cursor: "pointer",
                          padding: "2px 6px"
                        }}
                        onClick={() => handleRemoveStop(index)}
                      >
                        ✕ Remove
                      </button>
                    </div>
                    <div className="input-with-icon" style={{ position: "relative" }}>
                      <span className="stop-number-badge">{index + 1}</span>
                      <input
                        ref={attachStopAutocomplete(index)}
                        type="text"
                        className="form-control-custom"
                        placeholder={`Enter Stop ${index + 1} (Via) address`}
                        value={stop.address || ""}
                        onChange={(e) => handleStopChange(index, e.target.value)}
                        style={{ paddingLeft: "35px", paddingRight: "35px" }}
                      />
                      {stop.address && (
                        <i
                          className="fas fa-times"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#999",
                            zIndex: 10
                          }}
                          onClick={() => handleClearStop(index)}
                        ></i>
                      )}
                    </div>
                  </div>
                ))}

                {/* + Add Stop Button */}
                <div style={{ marginBottom: "6px" }}>
                  <button
                    type="button"
                    className="btn-add-stop"
                    onClick={handleAddStop}
                  >
                    <i className="fas fa-plus-circle"></i>
                    <span>+ Add Pickup / Stop</span>
                  </button>
                </div>

                {/* Drop-Off Location */}
                <div className="form-group-custom">
                  <label>Drop-Off Location</label>
                  <div
                    className="input-with-icon"
                    style={{ position: "relative" }}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    <input
                      ref={dropoffRef}
                      type="text"
                      name="dropoffLocation"
                      className="form-control-custom"
                      placeholder="Enter Drop-Off Location"
                      value={bookingData.dropoffLocation}
                      onChange={handleChange}
                      onFocus={() => setIsDropoffFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setIsDropoffFocused(false), 200)
                      }
                      style={{
                        paddingRight: "45px",
                        display:
                          !isDropoffFocused && bookingData.dropoffLocation
                            ? "none"
                            : "block",
                      }}
                      required
                    />
                    {!isDropoffFocused && bookingData.dropoffLocation && (
                      <div
                        className="form-control-custom"
                        style={{
                          height: "auto",
                          minHeight: "34px",
                          paddingRight: "45px",
                          paddingLeft: "35px",
                          paddingTop: "2px",
                          paddingBottom: "2px",
                          cursor: "text",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          backgroundColor: "var(--bg-offset)",
                          fontSize: "11px",
                          lineHeight: "1.2",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          setIsDropoffFocused(true);
                          setTimeout(() => {
                            if (dropoffRef.current) dropoffRef.current.focus();
                          }, 10);
                        }}
                      >
                        {bookingData.dropoffLocation}
                      </div>
                    )}
                    {bookingData.dropoffLocation && (
                      <i
                        className="fas fa-times"
                        style={{
                          position: "absolute",
                          right: "15px",
                          left: "auto",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#999",
                          padding: "10px",
                          pointerEvents: "auto",
                          zIndex: 10,
                        }}
                        onClick={() => {
                          setBookingData((prev) => ({
                            ...prev,
                            dropoffLocation: "",
                            dropoffLat: null,
                            dropoffLng: null,
                          }));
                          if (dropoffRef.current) dropoffRef.current.value = "";
                        }}
                      ></i>
                    )}
                  </div>
                </div>
                <div className="row">
                  {/* Passengers */}
                  <div className="col-12 col-md-6">
                    <div className="form-group-custom">
                      <label>Passengers</label>
                      <div className="input-with-icon">
                        <i className="fas fa-users"></i>
                        <input
                          type="number"
                          name="passengers"
                          className="form-control-custom"
                          value={bookingData.passengers}
                          onChange={handleChange}
                          min="1"
                          max="20"
                          required
                        />
                      </div>
                      {isNightPickupTime(bookingData.pickupTime) && (
                        <div style={{
                          fontSize: "11px",
                          color: "#b45309",
                          backgroundColor: "#fef3c7",
                          border: "1px solid #fde68a",
                          borderRadius: "6px",
                          padding: "3px 8px",
                          marginTop: "5px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          fontWeight: "600"
                        }}>
                          <span>🌙 Night Charge (9 PM - 5 AM): Applicable</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Luggage */}
                  {/* <div className="col-12 col-md-6">
                                        <div className="form-group-custom">
                                            <label>Bags</label>
                                            <div className="input-with-icon">
                                                <i className="fas fa-suitcase"></i>
                                                <input
                                                    type="number"
                                                    name="luggage"
                                                    className="form-control-custom"
                                                    value={bookingData.luggage}
                                                    onChange={handleChange}
                                                    min="0"
                                                    max="20"
                                                />
                                            </div>
                                        </div>
                                    </div> */}

                  {/* Extra Waiting Hours */}
                  <div className="col-12 col-md-6">
                    <div className="form-group-custom">
                      <label>Waiting</label>
                      <div className="input-with-icon select-wrapper">
                        <i className="far fa-clock"></i>
                        <select
                          name="waitingHours"
                          className="form-control-custom"
                          value={bookingData.waitingHours}
                          onChange={handleChange}
                        >
                          <option value="no-waiting">No</option>
                          {[...Array(12).keys()].map((i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} Hr{i + 1 > 1 ? "s" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <small
                        style={{
                          fontSize: "10px",
                          color: "#888",
                          display: "block",
                          marginTop: "4px",
                          lineHeight: "1.2",
                        }}
                      >
                        * First 2 hours free. Post that: Hatchback: ₹40/Hr,
                        Sedan: ₹60/Hr, SUV: ₹80/Hr.
                      </small>
                    </div>
                  </div>
                </div>

                {/* Search Button */}
                <button type="submit" className="search-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span> Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search"></i> Search
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Right Side: Map & Info */}
            <div className="booking-right">
              <div ref={mapRef} className="map-iframe"></div>

              {/* Floating Info Over Map */}
              <div className="distance-time-info">
                <div className="info-item">
                  <div className="info-icon distance">
                    <i className="fas fa-route"></i>
                  </div>
                  <div className="info-text">
                    <h6>Total Distance</h6>
                    <span>{distanceInfo.distance}</span>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon time">
                    <i className="far fa-clock"></i>
                  </div>
                  <div className="info-text">
                    <h6>Total Time</h6>
                    <span>{distanceInfo.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
